import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { AuthorizationService } from '@modules/authorization/services/authorization.service';
import { ContactsService } from '@modules/contacts/contacts.service';
import { ContactsResources } from '@resources/ru/commonModule/contacts.resources';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  tap,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '@modules/shared/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {
  GetContactInformationResponse,
  GetUserBotRedirectResponse,
} from '@codegen';
import { DefaultService } from '@codegen/service';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'chat-bot',
  templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss'],
  imports: [NgClass, NgTemplateOutlet, AsyncPipe, DragDropModule],
})
export class ChatBotComponent implements OnInit {
  private readonly elementRef = inject(ElementRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly router = inject(Router);
  private readonly resource = inject(DefaultService);
  private readonly contactsService = inject(ContactsService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly destroyRef = inject(DestroyRef);

  public isOpen: boolean = false;
  public isDragging: boolean = false;
  public isAuthorized: boolean = false;
  public isShowTelegram: boolean = false;
  public isShowViber: boolean = false;
  public contactsResources: ContactsResources = new ContactsResources();
  public contacts$: Observable<GetContactInformationResponse>;
  public email: string;
  public phone: string;
  public phoneLink: string;
  public bots: { [key: string]: SafeUrl } = {};
  public authorization$: Observable<boolean>;

  private dragEnd$ = new Subject<void>();

  readonly isInfoShow$ = this.store
    .select(state => state.documents.infoBlockForShow.isShowing)
    .pipe(takeUntilDestroyed(this.destroyRef));

  public ngOnInit(): void {
    this.isAuthorized = this.authorizationService.isAuthorized();

    this.email = this.contactsService.getSupportEmail();

    this.dragEnd$
      .pipe(
        delay(50),
        tap(() => (this.isDragging = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged(),
        tap(event => {
          if (event instanceof NavigationEnd) {
            const isAuthorized = this.authorizationService.isAuthorized();
            if (this.isAuthorized !== isAuthorized) {
              this.isAuthorized = isAuthorized;
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  public toggleChat(event: MouseEvent): void {
    if (this.isDragging) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.getBotLinks();
    }
  }

  onDragStarted(): void {
    this.isDragging = true;
  }

  onDragEnded(): void {
    this.dragEnd$.next();
  }

  public sanitizeUrl(url: SafeUrl | string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url as string);
  }

  private getBotLinks(): void {
    this.resource
      .informationUserBotRedirectUrl({
        bot: this.contactsResources.telegram.toLowerCase(),
      })
      .pipe(
        tap((response: GetUserBotRedirectResponse) => {
          if (response.errorInfo.error === '0') {
            this.isShowTelegram = true;
            this.bots[this.contactsResources.telegram.toLowerCase()] =
              response.redirectUrl;
          } else {
            this.isShowTelegram = false;
          }
        }),
        catchError(() => {
          this.isShowTelegram = false;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.resource
      .informationUserBotRedirectUrl({
        bot: this.contactsResources.viber.toLowerCase(),
      })
      .pipe(
        tap((response: GetUserBotRedirectResponse) => {
          if (response.errorInfo.error === '0') {
            this.isShowViber = true;
            this.bots[this.contactsResources.viber.toLowerCase()] =
              response.redirectUrl;
          } else {
            this.isShowViber = false;
          }
        }),
        catchError(() => {
          this.isShowViber = false;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.resource
      .informationContactForClient()
      .pipe(
        tap((response: GetContactInformationResponse) => {
          this.phone = response.phones[0]?.value;
          this.phoneLink = response.phones[0]?.link;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
